import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  assetValidationUrlSliceEquals,
  mergeAssetValidationUrl,
  parseAssetValidationParams
} from './assetValidationUrlSync';
import { headerTabValidations, TAB_VALIDATIONS } from './const';
import { AssetModel, AssetTypes, ParamGetAssetModels, TabType } from './type';
import { usePaging } from 'hooks/usePaging';
import { SIZE_VALIDATION_ASSET_MODEL } from 'const/paging';
import { getAssetModel, getAssetTypes, updateSubmittedEntries } from 'services/asset';
import {
  actionExportAssetModels,
  actionExportAssets,
  actionExportAssetTypes,
  actionExportRoomIssues,
  getAssetIssues
} from 'services/locations';
import { AssetIssues, RoomLevelIssues } from 'types/room';
import { FormEditSubmittedEntries } from './components/elements/ContentExtraTableValidation';
import { useAsyncCallback } from 'hooks/useAsyncCallback';
import { useFlash } from 'contexts/flash';
import { ParamGetAssetIssues, SearchType } from 'types/loctions';
import { findIndex, fromPairs } from 'lodash';
import { getListRoomLevelIssues, ParamsGetListRoomLevelIssues } from 'services/report';
import { SelectChangeEvent } from '@mui/material';
import { useFilteredRoomContext } from 'hooks/useFilteredRoomContext';

export const useInjection = () => {
  const { setFlash } = useFlash();
  const [searchParams, setSearchParams] = useSearchParams();
  const skipAvUrlHydrateRef = useRef(false);

  const initialAv = parseAssetValidationParams(searchParams);

  const [page, setPage] = useState<{
    assetModel: number;
    assetType: number;
    assetValidation: number;
    roomIssues: number;
  }>({
    assetModel: 1,
    assetType: 1,
    assetValidation: initialAv.page,
    roomIssues: 1
  });
  const [sortBy, setSortBy] = useState<{
    assetModel: string;
    assetType: string;
    assetValidation: string;
    roomIssues: string;
  }>({
    assetModel: '',
    assetType: '',
    assetValidation: initialAv.sort,
    roomIssues: ''
  });
  const [order, setOrder] = useState<{
    assetModel: 'desc' | 'asc' | undefined;
    assetType: 'desc' | 'asc' | undefined;
    assetValidation: 'desc' | 'asc' | undefined;
    roomIssues: 'desc' | 'asc' | undefined;
  }>({
    assetModel: undefined,
    assetType: undefined,
    assetValidation: initialAv.order,
    roomIssues: undefined
  });
  const [searchText, setSearchText] = useState<{
    assetModel: string;
    assetValidation: string;
    assetType: string;
    roomIssues: string;
  }>({
    assetModel: '',
    assetValidation: initialAv.keyword,
    assetType: '',
    roomIssues: ''
  });

  const [selectedCriticality, setSelectedCriticality] = useState(initialAv.criticality);

  const {
    pagingInfo: pagingInfoAssetModel,
    setCurrentPage: setCurrentPageAssetModel,
    setTotal: setTotalAssetModel
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });
  const {
    pagingInfo: pagingInfoAssetTypes,
    setCurrentPage: setCurrentPageAssetTypes,
    setTotal: setTotalAssetTypes
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });
  const {
    pagingInfo: pagingInfoAssetValidation,
    setCurrentPage: setCurrentPageAssetValidation,
    setTotal: setTotalAssetValidation
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL,
    currentPage: initialAv.page
  });
  const {
    pagingInfo: pagingInfoRoomLevelIssues,
    setCurrentPage: setCurrentPageInfoRoomLevelIssues,
    setTotal: setTotalInfoRoomLevelIssues
  } = usePaging({
    rowsPerPage: SIZE_VALIDATION_ASSET_MODEL
  });

  const [dateValidationAsset, setDateValidationAsset] = useState<{
    startDate?: string;
    endDate?: string;
  }>({
    startDate: initialAv.dateStart,
    endDate: initialAv.dateEnd
  });
  const [dataRoomLevelIssues, setDataRoomLevelIssues] = useState<RoomLevelIssues[]>([]);
  const [dataAssetModel, setDataAssetModel] = useState<AssetModel[]>([]);
  const [dataAssetTypes, setDataAssetTypes] = useState<AssetTypes[]>([]);
  const [dataAssetValidation, setDataAssetValidation] = useState<AssetIssues[]>([]);
  const [filterValidations, setFilterValidations] = useState<
    {
      key: string;
      value: string;
      label: string;
      level?: number;
    }[]
  >(initialAv.filters ?? []);
  const [filterRoomIssues, setFilterRoomIssues] = useState<
    {
      key: string;
      value: string;
      label: string;
      level?: number;
    }[]
  >();

  const [maintainanceSelectedDate, setSelectedMaintainanceDate] = useState<string | undefined>(
    initialAv.maintDate
  );
  const [maintainanceSelectedType, setSelectedMaintainanceType] = useState<string>(
    initialAv.maintType
  );

  const [searchType, setSearchType] = useState<SearchType>(initialAv.searchType);
  const [archiveRoomIssuesFilter, setArchiveRoomIssuesFilter] = useState<'active' | 'archived'>(
    'active'
  );

  useEffect(() => {
    setPage((prev) => ({ ...prev, roomIssues: 1 }));
    setCurrentPageInfoRoomLevelIssues(1);
  }, [archiveRoomIssuesFilter, setCurrentPageInfoRoomLevelIssues]);
  const [expandedSubmittedAssetId, setExpandedSubmittedAssetId] = useState<number | undefined>(
    initialAv.expandedSubmittedAssetId
  );
  const { systemDropdownValues: options, refreshOptions } = useFilteredRoomContext();

  const assetValidationUrlSlice = useMemo(
    () => ({
      page: page.assetValidation,
      keyword: searchText.assetValidation,
      sort: sortBy.assetValidation,
      order: order.assetValidation,
      dateStart: dateValidationAsset?.startDate ?? '',
      dateEnd: dateValidationAsset?.endDate ?? '',
      criticality: selectedCriticality,
      maintType: maintainanceSelectedType,
      maintDate: maintainanceSelectedDate,
      searchType,
      filters: !filterValidations?.length ? undefined : filterValidations,
      expandedSubmittedAssetId
    }),
    [
      page.assetValidation,
      searchText.assetValidation,
      sortBy.assetValidation,
      order.assetValidation,
      dateValidationAsset?.startDate,
      dateValidationAsset?.endDate,
      selectedCriticality,
      maintainanceSelectedType,
      maintainanceSelectedDate,
      searchType,
      filterValidations,
      expandedSubmittedAssetId
    ]
  );

  /** Browser back/forward or external URL: apply av_* into React state before paint. */
  useLayoutEffect(() => {
    if (skipAvUrlHydrateRef.current) {
      skipAvUrlHydrateRef.current = false;
      return;
    }
    const tabParam = searchParams.get('tab') || TAB_VALIDATIONS.ASSET_VALIDATIONS;
    if (tabParam !== TAB_VALIDATIONS.ASSET_VALIDATIONS) {
      return;
    }
    const parsed = parseAssetValidationParams(searchParams);
    setPage((p) =>
      p.assetValidation === parsed.page ? p : { ...p, assetValidation: parsed.page }
    );
    setCurrentPageAssetValidation(parsed.page);
    setSearchText((t) =>
      t.assetValidation === parsed.keyword ? t : { ...t, assetValidation: parsed.keyword }
    );
    setSortBy((s) =>
      s.assetValidation === parsed.sort ? s : { ...s, assetValidation: parsed.sort }
    );
    setOrder((o) =>
      o.assetValidation === parsed.order ? o : { ...o, assetValidation: parsed.order }
    );
    setDateValidationAsset((d) => {
      const start = parsed.dateStart;
      const end = parsed.dateEnd;
      if (d.startDate === start && d.endDate === end) return d;
      return { startDate: start, endDate: end };
    });
    setSelectedCriticality((c) => (c === parsed.criticality ? c : parsed.criticality));
    setSelectedMaintainanceType((m) => (m === parsed.maintType ? m : parsed.maintType));
    setSelectedMaintainanceDate((md) => (md === parsed.maintDate ? md : parsed.maintDate));
    setSearchType((st) => (st === parsed.searchType ? st : parsed.searchType));
    setFilterValidations((f) => {
      const next = parsed.filters ?? [];
      if (JSON.stringify(f) === JSON.stringify(next)) return f;
      return next;
    });
    setExpandedSubmittedAssetId((e) =>
      e === parsed.expandedSubmittedAssetId ? e : parsed.expandedSubmittedAssetId
    );
  }, [searchParams, setCurrentPageAssetValidation]);

  /** User-driven state → URL (shareable / reload-safe), only on Asset Validation tab. */
  useEffect(() => {
    const tabParam = searchParams.get('tab') || TAB_VALIDATIONS.ASSET_VALIDATIONS;
    if (tabParam !== TAB_VALIDATIONS.ASSET_VALIDATIONS) return;

    setSearchParams(
      (prev) => {
        const fromPrev = parseAssetValidationParams(prev);
        if (assetValidationUrlSliceEquals(assetValidationUrlSlice, fromPrev)) return prev;
        skipAvUrlHydrateRef.current = true;
        return mergeAssetValidationUrl(prev, assetValidationUrlSlice);
      },
      { replace: true }
    );
  }, [assetValidationUrlSlice, setSearchParams, searchParams]);

  const onChangeTab = useCallback(
    (tab: number) => {
      const nameTab = headerTabValidations.find((item) => item.value === tab)?.param;
      setSearchParams({ tab: nameTab || TAB_VALIDATIONS.ASSET_VALIDATIONS });
    },
    [setSearchParams]
  );

  const getInitDataAssetModel = useCallback(
    async (params: Partial<ParamGetAssetModels>) => {
      const dataModels = await getAssetModel(params);
      setDataAssetModel(dataModels.models);
      setTotalAssetModel(dataModels.total);
    },
    [setTotalAssetModel]
  );

  const getInitDataAssetTypes = useCallback(
    async (params: Partial<ParamGetAssetModels>) => {
      const data = await getAssetTypes(params);
      setDataAssetTypes(data.types);
      setTotalAssetTypes(data.total);
    },
    [setTotalAssetTypes]
  );

  const getInitDataAssetValidation = useCallback(
    async (paramsProps: Partial<ParamGetAssetIssues>) => {
      const dataAsset = await getAssetIssues(paramsProps);
      setDataAssetValidation(dataAsset.submittedAssets);
      setTotalAssetValidation(dataAsset.total);
      return dataAsset.submittedAssets;
    },
    [setTotalAssetValidation]
  );

  const getInitRoomLevelIssues = useCallback(
    async (paramsProps: Partial<ParamsGetListRoomLevelIssues>) => {
      const data = await getListRoomLevelIssues({ params: paramsProps });
      setDataRoomLevelIssues(data.roomIssue);
      setTotalInfoRoomLevelIssues(data.total);
    },
    [setTotalInfoRoomLevelIssues]
  );

  const { asyncCallback: asyncGetDataRoomLevelIssues } = useAsyncCallback(
    getInitRoomLevelIssues,
    []
  );
  const { asyncCallback: asyncGetInitDataAssetModel } = useAsyncCallback(getInitDataAssetModel, []);
  const { asyncCallback: asyncGetInitDataAssetTypes } = useAsyncCallback(getInitDataAssetTypes, []);
  const { asyncCallback: asyncGetInitDataAssetValidation } = useAsyncCallback(
    getInitDataAssetValidation,
    []
  );
  useEffect(() => {
    const filterParams = fromPairs(
      (filterRoomIssues ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.roomIssues,
      keyword: searchText.roomIssues || undefined,
      orderBy: sortBy.roomIssues || undefined,
      order: order.roomIssues || undefined,
      searchType,
      archived: archiveRoomIssuesFilter === 'archived',
      ...filterParams
    };
    asyncGetDataRoomLevelIssues(params);
  }, [
    asyncGetDataRoomLevelIssues,
    filterRoomIssues,
    order.roomIssues,
    page.roomIssues,
    searchText.roomIssues,
    sortBy.roomIssues,
    searchType,
    archiveRoomIssuesFilter
  ]);
  useEffect(() => {
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.assetModel,
      keyword: searchText.assetModel || undefined,
      orderBy: sortBy.assetModel || undefined,
      order: order.assetModel || undefined
    };
    asyncGetInitDataAssetModel(params);
  }, [
    asyncGetInitDataAssetModel,
    order.assetModel,
    page.assetModel,
    searchText.assetModel,
    sortBy.assetModel
  ]);

  useEffect(() => {
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.assetType,
      keyword: searchText.assetType || undefined,
      orderBy: sortBy.assetType || undefined,
      order: order.assetType || undefined
    };
    asyncGetInitDataAssetTypes(params);
  }, [
    asyncGetInitDataAssetTypes,
    order.assetType,
    page.assetType,
    searchText.assetType,
    sortBy.assetType
  ]);

  const exportAssetTypes = async (type: string) => {
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.assetType,
      keyword: searchText.assetType || undefined,
      orderBy: sortBy.assetType || undefined,
      order: order.assetType || undefined,
      exportType: type
    };
    await actionExportAssetTypes(params);
    setFlash({
      type: 'success',
      message:
        'Your report is being generated in the background. Please check the Background page to view the result.'
    });
  };
  const { asyncCallback: asyncExportAssetTypes } = useAsyncCallback(exportAssetTypes, [
    page.assetType,
    searchText.assetType,
    sortBy.assetType,
    order.assetType
  ]);

  const exportAssetModels = async (type: string) => {
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.assetModel,
      keyword: searchText.assetModel || undefined,
      orderBy: sortBy.assetModel || undefined,
      order: order.assetModel || undefined,
      exportType: type
    };
    await actionExportAssetModels(params);
    setFlash({
      type: 'success',
      message:
        'Your report is being generated in the background. Please check the Background page to view the result.'
    });
  };
  const { asyncCallback: asyncExportAssetModels } = useAsyncCallback(exportAssetModels, [
    order.assetModel,
    page.assetModel,
    searchText.assetModel,
    sortBy.assetModel
  ]);

  const exportRoomIssues = async (
    type: string,
    imageOption?: string,
    columns?: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => {
    const filterParams = fromPairs(
      (filterRoomIssues ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.roomIssues,
      keyword: searchText.roomIssues || undefined,
      orderBy: sortBy.roomIssues || undefined,
      order: order.roomIssues || undefined,
      exportType: type,
      imageOption: imageOption || '1st',
      columns,
      dropdownOptionColumns,
      singleFieldColumns,
      searchType,
      archived: archiveRoomIssuesFilter === 'archived',
      ...filterParams
    };
    await actionExportRoomIssues(params);
    setFlash({
      type: 'success',
      message:
        'Report generation has been queued. Download the file from the Background Jobs tab when it is ready.'
    });
  };
  const { asyncCallback: asyncExportRoomIssues } = useAsyncCallback(exportRoomIssues, [
    filterRoomIssues,
    order.roomIssues,
    page.roomIssues,
    searchText.roomIssues,
    sortBy.roomIssues,
    searchType,
    archiveRoomIssuesFilter
  ]);
  const fetchAssetValidation = useCallback(() => {
    const filterParams = fromPairs(
      (filterValidations ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.assetValidation,
      keyword: searchText.assetValidation || undefined,
      orderBy: sortBy.assetValidation || undefined,
      order: order.assetValidation || undefined,
      actionByDateMin: dateValidationAsset?.startDate || undefined,
      actionByDateMax: dateValidationAsset?.endDate || undefined,
      maintainanceType: maintainanceSelectedType === 'All' ? undefined : maintainanceSelectedType,
      maintainanceDate: maintainanceSelectedDate || undefined,
      searchType,
      criticality:
        selectedCriticality === '0' || !selectedCriticality ? undefined : selectedCriticality,
      ...filterParams
    };
    return asyncGetInitDataAssetValidation(params);
  }, [
    asyncGetInitDataAssetValidation,
    filterValidations,
    order.assetValidation,
    page.assetValidation,
    searchText.assetValidation,
    sortBy.assetValidation,
    dateValidationAsset,
    selectedCriticality,
    maintainanceSelectedType,
    maintainanceSelectedDate,
    searchType
  ]);

  useEffect(() => {
    fetchAssetValidation();
  }, [fetchAssetValidation]);

  const onSearch = useCallback(
    (value: string) => {
      const key = searchParams.get('tab') || TAB_VALIDATIONS.ASSET_VALIDATIONS;
      setPage((prev) => ({ ...prev, [key]: 1 }));
      if (key === TAB_VALIDATIONS.ASSET_VALIDATIONS) setCurrentPageAssetValidation(1);
      if (key === TAB_VALIDATIONS.ASSET_MODEL) setCurrentPageAssetModel(1);
      if (key === TAB_VALIDATIONS.ASSET_TYPES) setCurrentPageAssetTypes(1);
      if (key === TAB_VALIDATIONS.ROOM_ISSUES) setCurrentPageInfoRoomLevelIssues(1);
      setSearchText((prevState) => ({ ...prevState, [key]: value }));
    },
    [
      searchParams,
      setCurrentPageAssetModel,
      setCurrentPageAssetTypes,
      setCurrentPageAssetValidation,
      setCurrentPageInfoRoomLevelIssues
    ]
  );

  // Trigger Types has no entry in sortBy/order — it's a self-contained tab with
  // no sort UI (see components/modules/TriggerTypes) — so fall back to the
  // default tab's slot for any key that isn't one of the 4 sortable tabs.
  const sortOption = useMemo(() => {
    const rawKey = searchParams.get('tab') || TAB_VALIDATIONS.ASSET_VALIDATIONS;
    const key = (
      rawKey in sortBy ? rawKey : TAB_VALIDATIONS.ASSET_VALIDATIONS
    ) as keyof typeof sortBy;
    return {
      field: sortBy[key],
      order: order[key]
    };
  }, [order, searchParams, sortBy]);

  const handleSort = useCallback(
    (orderBy: string) => {
      const rawKey = searchParams.get('tab') || TAB_VALIDATIONS.ASSET_VALIDATIONS;
      const key = (
        rawKey in sortBy ? rawKey : TAB_VALIDATIONS.ASSET_VALIDATIONS
      ) as keyof typeof sortBy;
      const sortByNow = sortBy[key];
      const orderNow = order[key];
      if (orderBy === sortByNow) {
        if (orderNow === 'asc') {
          setOrder((prevState) => ({ ...prevState, [key]: 'desc' }));
        } else {
          setOrder((prevState) => ({ ...prevState, [key]: 'asc' }));
        }
      } else {
        setSortBy((prevState) => ({ ...prevState, [key]: orderBy }));
        setOrder((prevState) => ({ ...prevState, [key]: 'asc' }));
      }
    },
    [searchParams, sortBy, order]
  );

  const handleChangePage = useCallback(
    (value: number) => {
      const key = (searchParams.get('tab') || TAB_VALIDATIONS.ASSET_VALIDATIONS) as TabType;
      if (key === TAB_VALIDATIONS.ASSET_MODEL) {
        setCurrentPageAssetModel(value);
      }
      if (key === TAB_VALIDATIONS.ASSET_TYPES) {
        setCurrentPageAssetTypes(value);
      }
      if (key === TAB_VALIDATIONS.ASSET_VALIDATIONS) {
        setCurrentPageAssetValidation(value);
      }
      if (key === TAB_VALIDATIONS.ROOM_ISSUES) {
        setCurrentPageInfoRoomLevelIssues(value);
      }
      setPage((prevState) => ({ ...prevState, [key]: value }));
    },
    [
      searchParams,
      setCurrentPageAssetModel,
      setCurrentPageAssetTypes,
      setCurrentPageAssetValidation,
      setCurrentPageInfoRoomLevelIssues
    ]
  );

  const onEditSubmittedEntries = useCallback(
    async ({ value, rowActive }: { value: FormEditSubmittedEntries; rowActive: AssetIssues }) => {
      if (!rowActive?.id || !rowActive?.submitId) return;
      const data = (await updateSubmittedEntries({
        id: rowActive?.id,
        submitId: rowActive?.submitId,
        payload: {
          name: rowActive.name,
          typeId: rowActive.type.id,
          condition: rowActive.condition,
          criticalityKey: rowActive.criticalityKey || '',
          validated: true,
          hasIssue: rowActive.hasIssue,
          make: rowActive.make,
          modelId: rowActive.model.id,
          altText: rowActive.altText,
          entries:
            value.fields?.map((item) => ({
              value: item.value || '',
              fieldId: item.fieldId,
              dropdownValueId: item.dropdownValueId,
              dropdownOtherValue: item.dropdownOtherValue,
              deleteDropdownId: item.deleteDropdownId
            })) || []
        }
      })) as { submittedAsset: AssetIssues };
      setFlash({ type: 'success', message: 'Updated successful' });
      await fetchAssetValidation();
      setDataAssetValidation((prevState) =>
        prevState.map((item) => {
          if (item.id === data.submittedAsset.id) {
            return {
              ...item,
              validated: true
            };
          }
          return item;
        })
      );
    },
    [setFlash, fetchAssetValidation]
  );
  const { asyncCallback: asyncOnEditSubmittedEntries } = useAsyncCallback(
    onEditSubmittedEntries,
    []
  );

  const handleFilterValidation = useCallback(
    async (value?: { key: string; value: string; label: string; level?: number }) => {
      if (!value) return;
      setPage((prev) => ({ ...prev, assetValidation: 1 }));
      setCurrentPageAssetValidation(1);
      setFilterValidations((prevState) => {
        let nextState: typeof prevState;

        if (!prevState) {
          nextState = [value];
        } else {
          const index = findIndex(prevState, { key: value.key });
          if (index !== -1) {
            const existingItem = prevState[index];
            if (
              (!value?.level && existingItem.value === value.value) ||
              Number(value?.level) === Number(existingItem?.level)
            ) {
              nextState = [...prevState.slice(0, index), ...prevState.slice(index + 1)];
            } else {
              const updated = [...prevState];
              updated[index] = value;
              nextState = updated;
            }
          } else {
            nextState = [...prevState, value];
          }
        }
        return nextState;
      });
    },
    [setCurrentPageAssetValidation]
  );
  const handleFilterRoomIssues = useCallback(
    async (value?: { key: string; value: string; label: string; level?: number }) => {
      if (!value) return;
      setPage((prev) => ({ ...prev, roomIssues: 1 }));
      setCurrentPageInfoRoomLevelIssues(1);
      setFilterRoomIssues((prevState) => {
        let nextState: typeof prevState;

        if (!prevState) {
          nextState = [value];
        } else {
          const index = findIndex(prevState, { key: value.key });
          if (index !== -1) {
            const existingItem = prevState[index];
            if (
              (!value?.level && existingItem.value === value.value) ||
              Number(value?.level) === Number(existingItem?.level)
            ) {
              nextState = [...prevState.slice(0, index), ...prevState.slice(index + 1)];
            } else {
              const updated = [...prevState];
              updated[index] = value;
              nextState = updated;
            }
          } else {
            nextState = [...prevState, value];
          }
        }
        return nextState;
      });
    },
    [setCurrentPageInfoRoomLevelIssues]
  );

  const handleSelectGenerate = async (
    type: 'pdf' | 'xlsx',
    imageOption: string,
    columns?: string[],
    propertiesColumns?: string[],
    dropdownOptionColumns?: number[],
    singleFieldColumns?: number[]
  ) => {
    const filterParams = fromPairs(
      (filterValidations ?? [])
        .filter((item) => item.key && item.value)
        .map((item) => [item.key, item.value])
    );
    const params = {
      size: SIZE_VALIDATION_ASSET_MODEL,
      page: page.assetValidation,
      keyword: searchText.assetValidation || undefined,
      orderBy: sortBy.assetValidation || undefined,
      order: order.assetValidation || undefined,
      actionByDateMin: dateValidationAsset?.startDate || undefined,
      actionByDateMax: dateValidationAsset?.endDate || undefined,
      maintainanceType: maintainanceSelectedType === 'All' ? undefined : maintainanceSelectedType,
      maintainanceDate: maintainanceSelectedDate || undefined,
      searchType,
      criticality:
        selectedCriticality === '0' || !selectedCriticality ? undefined : selectedCriticality,
      exportType: type,
      imageOption,
      columns,
      propertiesColumns,
      dropdownOptionColumns,
      singleFieldColumns,
      ...filterParams
    };
    await actionExportAssets(params);
    setFlash({
      type: 'success',
      message:
        'Your report is being generated in the background. Please check the Background page to view the result.'
    });
  };

  const { asyncCallback: asyncHandleSelectGenerate } = useAsyncCallback(handleSelectGenerate, [
    page.assetValidation,
    searchText.assetValidation,
    sortBy.assetValidation,
    order.assetValidation,
    dateValidationAsset,
    maintainanceSelectedType,
    maintainanceSelectedDate,
    searchType,
    selectedCriticality,
    filterValidations
  ]);

  return {
    options,
    pagingInfoAssetModel,
    pagingInfoAssetTypes,
    pagingInfoAssetValidation,
    pagingInfoRoomLevelIssues,
    setCurrentPageAssetTypes,
    dataAssetTypes,
    dataAssetValidation,
    onChangeTab,
    searchParams,
    dataAssetModel,
    onSearch,
    handleChangePage,
    searchText,
    handleSort,
    sortOption,
    maintainanceSelectedDate,
    maintainanceSelectedType,
    setSelectedMaintainanceDate,
    setSelectedMaintainanceType,
    refreshDataModels: useCallback(() => {
      const params = {
        size: SIZE_VALIDATION_ASSET_MODEL,
        page: page.assetModel,
        keyword: searchText.assetModel || undefined,
        orderBy: sortBy.assetModel || undefined,
        order: order.assetModel || undefined
      };
      asyncGetInitDataAssetModel(params);
    }, [
      asyncGetInitDataAssetModel,
      order.assetModel,
      page.assetModel,
      searchText.assetModel,
      sortBy.assetModel
    ]),
    refreshDataTypes: useCallback(() => {
      const params = {
        size: SIZE_VALIDATION_ASSET_MODEL,
        page: page.assetType,
        keyword: searchText.assetType || undefined,
        orderBy: sortBy.assetType || undefined,
        order: order.assetType || undefined
      };
      asyncGetInitDataAssetTypes(params);
    }, [
      asyncGetInitDataAssetTypes,
      order.assetType,
      page.assetType,
      searchText.assetType,
      sortBy.assetType
    ]),
    refreshDataValidation: fetchAssetValidation,
    refreshDataRoomLevelIssues: useCallback(() => {
      const filterParams = fromPairs(
        (filterRoomIssues ?? [])
          .filter((item) => item.key && item.value)
          .map((item) => [item.key, item.value])
      );
      const params = {
        size: SIZE_VALIDATION_ASSET_MODEL,
        page: page.roomIssues,
        keyword: searchText.roomIssues || undefined,
        orderBy: sortBy.roomIssues || undefined,
        order: order.roomIssues || undefined,
        searchType,
        archived: archiveRoomIssuesFilter === 'archived',
        ...filterParams
      };
      asyncGetDataRoomLevelIssues(params);
    }, [
      asyncGetDataRoomLevelIssues,
      filterRoomIssues,
      order.roomIssues,
      page.roomIssues,
      searchText.roomIssues,
      sortBy.roomIssues,
      searchType,
      archiveRoomIssuesFilter
    ]),
    onEditSubmittedEntries: asyncOnEditSubmittedEntries,
    tab: headerTabValidations.find((item) => item.param === searchParams.get('tab'))?.value || 0,
    filterValidations,
    setFilterValidations: handleFilterValidation,
    setFilterRoomIssues: handleFilterRoomIssues,
    filterRoomIssues,
    setDataAssetValidation,
    dataRoomLevelIssues,
    dateValidationAsset,
    setDateValidationAsset,
    asyncHandleSelectGenerate,
    selectedCriticality,
    onFilterCriticality: useCallback((e: SelectChangeEvent<unknown>) => {
      setSelectedCriticality(e.target.value as string);
    }, []),
    searchType,
    setSearchType,
    archiveRoomIssuesFilter,
    setArchiveRoomIssuesFilter,
    asyncExportAssetTypes,
    asyncExportAssetModels,
    asyncExportRoomIssues,
    expandedSubmittedAssetId,
    setExpandedSubmittedAssetId,
    refreshOptions
  };
};
